import * as React from "react";
import { FamilyMember, RootState } from "../../types";
import { useFormik, FormikErrors } from "formik";
import * as Yup from "yup";
import { Organization } from "../../organizations/types";
import { useSelector, useDispatch } from "react-redux";
import { put } from "../../../utils/fetch";
import { getFamilyMembers } from "../actions";

interface Props {
  familyMember: FamilyMember;
  clearEditing: () => void;
}

const validationSchema = Yup.object().shape({
  phone: Yup.string().test(
    "len",
    "Please enter valid phone number.",
    val => (val && val.replace(/[^\d]/g, "").length === 10) || !val
  )
});

export default function FamilyMemberEditFormik({ familyMember, clearEditing }: Props) {
  const dispatch = useDispatch();
  const currentOrganization: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );

  const [previousPhone, setPreviousPhone] = React.useState<string>("");

  const updatePhone = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    const value = e.target.value;
    setFieldValue("phone", formatPhone(value), true);
  };

  const formatPhone = (value: string, forceUpdate?: boolean) => {
    let formattedvalue = "";
    const currentValue = value.replace(/[^\d]/g, "");
    const cvLength = currentValue.length;

    if (previousPhone || value.length > previousPhone.replace(/[^\d]/g, "").length || forceUpdate) {
      if (cvLength <= 3) formattedvalue = currentValue;
      if (cvLength >= 4 && cvLength <= 6)
        formattedvalue = `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
      if (cvLength > 6)
        formattedvalue = `(${currentValue.slice(0, 3)}) ${currentValue.slice(
          3,
          6
        )}-${currentValue.slice(6, 10)}`;
    } else {
      formattedvalue = value;
    }
    if (!forceUpdate) setPreviousPhone(formattedvalue);
    return formattedvalue;
  };

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    setErrors,
    touched,
    dirty,
    isValid,
    initialTouched,
    setFieldValue,
    isSubmitting
  } = useFormik({
    initialValues: {
      name: familyMember.name ? familyMember.name : "",
      relation: familyMember.relation ? familyMember.relation : "",
      phone: familyMember.phone ? formatPhone(familyMember.phone, true) : ""
    },
    validationSchema,
    async onSubmit(values) {
      dispatch({ type: "patient/setLoading", isLoading: true });
      const url = `/api/organization/${currentOrganization?.id}/patients/${familyMember.patientId}/familyMembers/${familyMember.id}/`;
      const resp = await put<FamilyMember>(url, values);
      if (resp.error) {
        setErrors(resp.error.fieldErrors as FormikErrors<any>);
        dispatch({ type: "patient/setLoading", isLoading: false });
      } else if (resp.data) {
        clearEditing();
        if (familyMember.patientId) dispatch(getFamilyMembers(familyMember.patientId));
      }
    }
  });

  return (
    <form onSubmit={handleSubmit} className="flex-grid align-center full-width">
      <div className="flex-1">
        <input
          id="relation"
          name="relation"
          type="text"
          onChange={handleChange}
          value={values.relation}
          className="mr-15"
        />
        {errors.relation && touched.relation && (
          <div className="formik-error">{errors.relation}</div>
        )}
      </div>
      <div className="flex-1">
        <input
          id="name"
          name="name"
          type="text"
          className={"mr-15" + errors.name && touched.name ? "text-input error" : "text-input"}
          onChange={handleChange}
          value={values.name}
        />
        {errors.name && touched.name && <div className="formik-error">{errors.name}</div>}
      </div>
      <div className="flex-1">
        <input
          id="phone"
          name="phone"
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePhone(e, setFieldValue)}
          className={"mr-15" + errors.phone && touched.phone ? "text-input error" : "text-input"}
          value={values.phone}
        />
        {errors.phone && touched.phone && <div className="formik-error">{errors.phone}</div>}
      </div>
      <div className="action double">
        <button className="mr-10" onClick={() => clearEditing()}>
          Cancel
        </button>
        <button
          type="submit"
          className={dirty && isValid ? "highlight" : ""}
          disabled={!initialTouched || !dirty || !isValid || isSubmitting}
        >
          Save
        </button>
      </div>
    </form>
  );
}
